/**
 * Central client wiring. Builds the @wg/hooks AuthCore and the @wg/api-client
 * http clients, attaches them, and exposes everything the rest of the app
 * needs. One module so the chicken-and-egg between core and http is solved
 * exactly once.
 */
import { createHttpClient } from '@wg/api-client';
import { createAuthCore } from '@wg/hooks/auth';
import { createContentApi } from '@wg/api/content';
import config from '../../config/env.json';
import packageJson from '../../package.json';
import logger from '../utils/logger';

const APP_BUILD = '0';
const LANG_STORAGE_KEY = 'selected-language';

const getOsVersion = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.userAgent || 'unknown';
};

const getSelectedLanguage = (): string => {
  if (typeof window === 'undefined') return 'en-US';
  try {
    const picked = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (picked) return picked;
  } catch {
    /* localStorage may be unavailable (e.g. private mode) — fall through */
  }
  return navigator.language || 'en-US';
};

// Storage adapter — synchronous localStorage shim that matches @wg/hooks'
// StorageAdapter interface. RN injects MMKV with the same surface.
const storage = {
  getString(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      console.error('[storage] getItem', key, err);
      return null;
    }
  },
  setString(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      console.error('[storage] setItem', key, err);
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.error('[storage] remove', key, err);
    }
  },
  getBoolean(key: string): boolean {
    try {
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  },
  setBoolean(key: string, value: boolean): void {
    try {
      localStorage.setItem(key, value ? 'true' : 'false');
    } catch {
      /* noop */
    }
  },
  clearAll(): void {
    try {
      localStorage.clear();
    } catch {
      /* noop */
    }
  },
};

export const auth = createAuthCore({
  storage,
  logger,
  storageKey: 'auth',
  getAppInfo: () => ({
    appName: 'wunderguide-web',
    appVersion: packageJson.version,
    deviceOs: 'web',
    osVersion: getOsVersion(),
  }),
});

const { httpClient, unauthClient } = createHttpClient({
  baseURL: config.BASE_URL,
  userAgent: config.USER_AGENT,
  appId: 'wunderguide-web',
  appVersion: packageJson.version,
  appBuild: APP_BUILD,
  osName: 'web',
  osVersion: getOsVersion(),
  getAuthToken: () => auth.getAuthToken(),
  tryRefreshAuthToken: () => auth.tryRefreshAuthToken(),
  getSelectedLanguage,
  logger,
  authedTimeoutMs: 20000,
  unauthedTimeoutMs: 20000,
});

// CRITICAL: api-client puts `User-Agent` in COMMON_HEADERS for parity with
// the RN app, but on web `User-Agent` is a forbidden header per the Fetch
// spec. Chrome/Firefox/desktop Safari silently strip it; iOS Safari instead
// lists `user-agent` in the CORS preflight `Access-Control-Request-Headers`.
// Caddy's allow-list doesn't (and shouldn't need to) include `user-agent`,
// so the preflight passes but the actual POST is blocked → signin and guest
// silently fail on iPhone while every desktop browser works. Drop it here.
for (const c of [httpClient, unauthClient]) {
  delete (c.defaults.headers as Record<string, unknown>)['User-Agent'];
  delete (c.defaults.headers.common as Record<string, unknown> | undefined)?.[
    'User-Agent'
  ];
}

auth.attachClients({ httpClient, unauthClient });

// Storage keys that are tied to a specific user/session. When the JWT's
// `sub` changes (guest→authed signin into a different account, or signin
// switching accounts) drop them so we don't leak the previous user's
// cached profile or prompt list into the new account's UI. Auth-related
// keys ('auth') are managed inside the core itself.
const USER_SCOPED_STORAGE_KEYS = ['user', 'suggested_prompts_v1'];

let lastSeenUserId: string | null = null;
auth.getSessionInfo().then(info => {
  lastSeenUserId = info.userId;
});
auth.onAuthEvent(async event => {
  try {
    const info = await auth.getSessionInfo();
    const prev = lastSeenUserId;
    const next = info.userId;
    if (event === 'sessionCleared' || prev !== next) {
      USER_SCOPED_STORAGE_KEYS.forEach(k => storage.remove(k));
    }
    lastSeenUserId = next;
  } catch {
    /* noop */
  }
});

export const contentApi = createContentApi(httpClient);

export { httpClient, unauthClient };
